import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';

import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useEventsContext } from '#src/contexts/events/index.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';

import type {
  UpdateUserSettingsHideBalanceMutation,
  UpdateUserSettingsHideBalanceMutationVariables,
} from './__generated__/useToggleHideBalance.graphql.ts';

const UPDATE_USER_SETTINGS_HIDE_BALANCE_MUTATION = gql`
  mutation UpdateUserSettingsHideBalanceMutation(
    $input: updateUserSettingsInput!
  ) {
    updateUserSettings(input: $input) {
      userSettings {
        id
        hideBalance
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdateUserSettingsHideBalanceMutation,
  UpdateUserSettingsHideBalanceMutationVariables
>;

const useToggleHideBalanceEvent = () => {
  const { identify } = useEventsContext();
  const track = useEvents();
  return useCallback(
    (hidden: boolean) => {
      identify('', {
        traits: {
          balance_revealed: !hidden,
        },
      });
      track('Toggle Hide Balance', { hidden });
    },
    [identify, track]
  );
};

export const useToggleHideBalance = () => {
  const { currentUser } = useCurrentUserContext();
  const trackToggleHideBalance = useToggleHideBalanceEvent();
  const [mutate, { loading }] = useMutation(
    UPDATE_USER_SETTINGS_HIDE_BALANCE_MUTATION,
    {
      optimisticResponse: {
        updateUserSettings: {
          __typename: 'updateUserSettingsPayload',
          userSettings: {
            __typename: 'UserSettings',
            id: currentUser!.userSettings.id,
            hideBalance: !currentUser?.userSettings?.hideBalance,
          },
          errors: [],
        },
      },
    }
  );

  const hideBalance = currentUser?.userSettings?.hideBalance;

  const toggleHideBalance = useCallback(() => {
    mutate({
      variables: {
        input: {
          hideBalance: !hideBalance,
        },
      },
    });
    trackToggleHideBalance(!hideBalance);
  }, [hideBalance, trackToggleHideBalance, mutate]);

  return {
    hideBalance: currentUser?.userSettings?.hideBalance,
    toggleHideBalance,
    loading,
  };
};
